import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../lib/firebase';

/**
 * Anything other than the literal 'prod' is non-prod — the same defensive
 * comparison `CheckoutButton` uses for the SDK env.
 *
 * This constant is a RUNTIME convenience only (e.g. the belt-and-braces
 * `IS_MOCK_GATEWAY &&` on the `<Route>` in App.tsx). It does NOT drop the
 * mock route or its chunk from a production build: Rollup only folds a
 * literal `import.meta.env.X` comparison into a compile-time-provable dead
 * branch when it's inline, in the same module, guarding the dynamic
 * import/call site itself — an imported cross-module boolean like this one
 * isn't resolved early enough for that analysis. The actual build-time
 * elimination comes from the inline `import.meta.env.VITE_AIRWALLEX_ENV`
 * checks in App.tsx (guarding the `lazy()` call) and CheckoutButton.tsx
 * (guarding the mock diversion) — see the comments there.
 */
export const IS_MOCK_GATEWAY = import.meta.env.VITE_AIRWALLEX_ENV !== 'prod';

export const MOCK_CHECKOUT_PATH = '/mock-checkout/:intentId';
export const mockCheckoutUrl = (intentId: string): string => `/mock-checkout/${intentId}`;

export interface MockIntentView {
  intentId: string;
  company: 'WSS' | 'SMART' | 'PINKI';
  amount: number;
  currency: 'SGD';
  merchantOrderId: string;
  returnUrl: string;
  status: 'REQUIRES_PAYMENT' | 'SUCCEEDED' | 'DECLINED';
  quoteId: string;
  surchargeAmount: string;
  method: string;
}

export interface MockConfirmResult {
  status: 'SUCCEEDED' | 'DECLINED';
  webhookStatus: number;
  returnUrl: string;
}

export const fetchMockIntent = async (intentId: string): Promise<MockIntentView> => {
  const callable = httpsCallable<{ intentId: string }, MockIntentView>(
    functions,
    'getMockPaymentIntent',
  );
  return (await callable({ intentId })).data;
};

export const confirmMockPayment = async (
  intentId: string,
  outcome: 'PAY' | 'DECLINE',
): Promise<MockConfirmResult> => {
  const callable = httpsCallable<
    { intentId: string; outcome: 'PAY' | 'DECLINE' },
    MockConfirmResult
  >(functions, 'confirmMockPayment');
  return (await callable({ intentId, outcome })).data;
};
